import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, AlertTriangle, Users, Clock, TrendingUp, AlertCircle, Calendar as CalendarIcon, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const OpsSchedule = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [unscheduledProjects, setUnscheduledProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectServices, setProjectServices] = useState([]);
  const [selectedServiceForPhase, setSelectedServiceForPhase] = useState(null);
  const [aiPhaseSuggestions, setAiPhaseSuggestions] = useState([]);
  const [filterProject, setFilterProject] = useState('all_projects');
  const [filterPhase, setFilterPhase] = useState('all_phases');
  const [formData, setFormData] = useState({
    title: '',
    scheduled_start_time: '09:00',
    estimated_hours: '2',
    priority: 'medium',
    project_service_id: '',
    phase_label: ''
  });

  // Generate stable pastel color from phase label
  const getPhaseColor = (phaseLabel) => {
    if (!phaseLabel) return 'bg-slate-100 text-slate-700 border-slate-300';
    
    // Simple hash function for consistent colors
    let hash = 0;
    for (let i = 0; i < phaseLabel.length; i++) {
      hash = phaseLabel.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const colors = [
      'bg-purple-100 text-purple-700 border-purple-300',
      'bg-blue-100 text-blue-700 border-blue-300',
      'bg-green-100 text-green-700 border-green-300',
      'bg-amber-100 text-amber-700 border-amber-300',
      'bg-pink-100 text-pink-700 border-pink-300',
      'bg-cyan-100 text-cyan-700 border-cyan-300',
      'bg-indigo-100 text-indigo-700 border-indigo-300',
      'bg-rose-100 text-rose-700 border-rose-300',
    ];
    
    return colors[Math.abs(hash) % colors.length];
  };

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tasksRes, conflictsRes, teamRes, projectsRes] = await Promise.all([
        api.get('/ops/tasks'),
        api.get('/ops/conflicts?resolved=false'),
        api.get('/ops/team?status=active'),
        api.get('/projects')
      ]);
      setTasks(tasksRes.data || []);
      setConflicts(conflictsRes.data || []);
      setTeamMembers(teamRes.data || []);
      
      // Filter for projects with estimate data but no scheduled assignments
      const allProjects = projectsRes.data || [];
      const unscheduled = allProjects.filter(p => 
        p.estimate_id && 
        p.estimated_labour_hours && 
        p.status !== 'completed'
      );
      setUnscheduledProjects(unscheduled);
    } catch (error) {
      console.error('Error fetching schedule data:', error);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const getWeekDays = () => {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay() + 1); // Start from Monday
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getWeekRange = () => {
    const days = getWeekDays();
    const start = days[0];
    const end = days[6];
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${end.getFullYear()}`;
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getTasksForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    let filteredTasks = tasks.filter(task => task.scheduled_date === dateStr);
    
    // Apply project filter
    if (filterProject && filterProject !== 'all_projects') {
      filteredTasks = filteredTasks.filter(task => task.project_id === filterProject);
    }
    
    // Apply phase filter
    if (filterPhase && filterPhase !== 'all_phases') {
      filteredTasks = filteredTasks.filter(task => task.phase_label === filterPhase);
    }
    
    return filteredTasks;
  };

  const getAssigneeName = (assignedTo) => {
    if (!assignedTo || assignedTo.length === 0) return 'Unassigned';
    const member = teamMembers.find(m => m.id === assignedTo[0]);
    return member?.name || 'Unassigned';
  };

  const handleAddToSchedule = async (date, project = null) => {
    setSelectedDate(date);
    setSelectedProject(project);
    setAddDialogOpen(true);
    
    // If project selected, fetch its services
    if (project?.id) {
      try {
        const response = await api.get(`/projects/${project.id}/services`);
        setProjectServices(response.data || []);
        setFormData({
          ...formData,
          title: project.project_name || '',
          project_id: project.id
        });
      } catch (error) {
        console.error('Error fetching project services:', error);
      }
    } else {
      setProjectServices([]);
    }
  };

  const handleProjectChange = async (projectId) => {
    const project = unscheduledProjects.find(p => p.id === projectId);
    setSelectedProject(project);
    
    if (projectId) {
      try {
        const response = await api.get(`/projects/${projectId}/services`);
        setProjectServices(response.data || []);
      } catch (error) {
        console.error('Error fetching project services:', error);
        setProjectServices([]);
      }
    } else {
      setProjectServices([]);
    }
  };

  const handleServiceChange = (serviceId) => {
    setFormData({ ...formData, project_service_id: serviceId, phase_label: '' });
    
    const service = projectServices.find(s => s.id === serviceId);
    setSelectedServiceForPhase(service);
    
    // Generate AI phase suggestions
    if (service && service.remaining_units > 0) {
      generatePhaseSuggestions(service);
    } else {
      setAiPhaseSuggestions([]);
    }
  };

  const generatePhaseSuggestions = (service) => {
    const remaining = service.remaining_units;
    const billed = service.billed_units;
    const suggestions = [];
    
    // Suggest phases based on remaining units
    if (remaining > 0) {
      // Calculate optimal phase size (5-10 units for larger projects, or 1/3 of remaining)
      let optimalPhaseSize = Math.min(10, Math.max(5, Math.ceil(remaining / 3)));
      
      // For small remaining, suggest the full amount
      if (remaining <= 10) {
        optimalPhaseSize = remaining;
      }
      
      const phaseNumber = Math.floor(billed / optimalPhaseSize) + 1;
      const startUnit = billed + 1;
      const endUnit = Math.min(billed + optimalPhaseSize, service.total_units);
      
      suggestions.push({
        label: `Phase ${phaseNumber} - ${service.unit_label}s ${startUnit}-${endUnit} (${endUnit - startUnit + 1} units)`,
        units: endUnit - startUnit + 1
      });
      
      // Suggest half of remaining
      if (remaining > 10) {
        const halfSize = Math.ceil(remaining / 2);
        suggestions.push({
          label: `Phase ${phaseNumber} - First ${halfSize} ${service.unit_label}s`,
          units: halfSize
        });
      }
      
      // Suggest all remaining
      if (remaining > 1) {
        suggestions.push({
          label: `Final Phase - All ${remaining} ${service.unit_label}s`,
          units: remaining
        });
      }
    }
    
    setAiPhaseSuggestions(suggestions);
  };


  const handleCreateScheduledTask = async () => {
    if (!formData.title) {
      toast.error('Please enter a task title');
      return;
    }

    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const endTime = calculateEndTime(formData.scheduled_start_time, parseFloat(formData.estimated_hours));
      
      const taskData = {
        title: formData.title,
        estimated_hours: parseFloat(formData.estimated_hours),
        priority: formData.priority,
        scheduled_date: dateStr,
        scheduled_start_time: formData.scheduled_start_time,
        scheduled_end_time: endTime
      };

      // Add project and phase info if selected
      if (formData.project_id) {
        taskData.project_id = formData.project_id;
      }
      if (formData.project_service_id) {
        taskData.project_service_id = formData.project_service_id;
      }
      if (formData.phase_label) {
        taskData.phase_label = formData.phase_label;
      }
      
      await api.post('/ops/tasks', taskData);
      
      toast.success(formData.phase_label 
        ? `Task added to schedule - ${formData.phase_label}`
        : 'Task added to schedule'
      );
      setAddDialogOpen(false);
      setFormData({ 
        title: '', 
        scheduled_start_time: '09:00', 
        estimated_hours: '2', 
        priority: 'medium',
        project_service_id: '',
        phase_label: '',
        project_id: ''
      });
      setProjectServices([]);
      setSelectedProject(null);
      fetchData();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  const calculateEndTime = (startTime, hours) => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const totalMinutes = startHour * 60 + startMin + (hours * 60);
    const endHour = Math.floor(totalMinutes / 60) % 24;
    const endMin = totalMinutes % 60;
    return `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
  };

  const getCardColor = (task) => {
    if (task.status === 'unassigned') return 'bg-slate-200 border-slate-300';
    if (task.status === 'completed') return 'bg-green-100 border-green-300';
    if (task.priority === 'urgent') return 'bg-red-100 border-red-300';
    if (task.priority === 'high') return 'bg-orange-100 border-orange-300';
    return 'bg-blue-100 border-blue-300';
  };

  const weekDays = getWeekDays();
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Calculate quick stats
  const scheduledHours = tasks.reduce((sum, task) => {
    if (task.scheduled_date) {
      return sum + (task.estimated_hours || 0);
    }
    return sum;
  }, 0);

  const utilization = teamMembers.length > 0 
    ? Math.round((scheduledHours / (teamMembers.length * 40)) * 100) 
    : 0;

  // Get team availability status
  const getTeamAvailability = () => {
    return teamMembers.map(member => {
      const memberTasks = tasks.filter(task => 
        task.assigned_to && task.assigned_to.includes(member.id) && task.status === 'in_progress'
      );
      return {
        ...member,
        isAvailable: memberTasks.length === 0
      };
    });
  };

  const teamAvailability = getTeamAvailability();

  return (
    <>
      <div className="flex gap-6">
        {/* Main Schedule Content */}
        <div className="flex-1 space-y-6">
        {/* Description */}
        <div>
          <p className="text-slate-600">Manage weekly schedules and resolve conflicts</p>
        </div>

        {/* Conflicts Alert */}
        {conflicts.length > 0 && (
          <Card className="border-2 border-amber-300 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-2">Scheduling Conflicts Detected</h3>
                  <ul className="space-y-1 text-sm text-slate-700 mb-4">
                    {conflicts.slice(0, 3).map(conflict => (
                      <li key={conflict.id}>• {conflict.description}</li>
                    ))}
                  </ul>
                  <Button variant="default" className="bg-[#1e3a5f] hover:bg-[#152a47]">
                    Resolve Conflicts
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

      {/* Calendar Controls */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={goToPreviousWeek}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="font-semibold text-lg">{getWeekRange()}</span>
                <Button variant="ghost" size="sm" onClick={goToNextWeek}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <Button variant="outline" size="sm" onClick={goToToday}>
                Today
              </Button>
              
              {/* Filters */}
              <div className="flex items-center gap-2 ml-4 pl-4 border-l border-slate-300">
                <Select value={filterProject} onValueChange={setFilterProject}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by Project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_projects">All Projects</SelectItem>
                    {unscheduledProjects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.project_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {filterProject && filterProject !== 'all_projects' && (
                  <Select value={filterPhase} onValueChange={setFilterPhase}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by Phase" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_phases">All Phases</SelectItem>
                      {[...new Set(tasks
                        .filter(t => t.project_id === filterProject && t.phase_label)
                        .map(t => t.phase_label)
                      )].map(phase => (
                        <SelectItem key={phase} value={phase}>
                          <Badge variant="outline" className={`mr-2 ${getPhaseColor(phase)}`}>
                            {phase}
                          </Badge>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                
                {(filterProject && filterProject !== 'all_projects') && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => { setFilterProject('all_projects'); setFilterPhase('all_phases'); }}
                    className="text-xs"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>
            <Button className="bg-[#247C75] hover:bg-[#1d6259]">
              <Plus className="h-4 w-4 mr-2" />
              Add to Schedule
            </Button>
          </div>

          {/* Week Grid */}
          <div className="grid grid-cols-7 gap-2">
            {weekDays.map((date, idx) => (
              <div key={idx} className="min-h-[400px]">
                {/* Day Header */}
                <div className={`text-center p-3 rounded-t-lg ${isToday(date) ? 'bg-blue-100' : 'bg-slate-100'}`}>
                  <div className="text-sm font-semibold text-slate-900">
                    {date.toLocaleDateString('en-US', { weekday: 'long' })}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>

                {/* Tasks Container */}
                <div className="space-y-2 p-2 bg-slate-50 rounded-b-lg min-h-[350px]">
                  {getTasksForDay(date).map(task => (
                    <div
                      key={task.id}
                      className={`p-3 rounded-lg border ${getCardColor(task)} cursor-pointer hover:shadow-md transition-shadow`}
                    >
                      <div className="text-xs font-medium text-slate-700 mb-1">
                        {task.scheduled_start_time || '09:00'}
                      </div>
                      <div className="text-sm font-semibold text-slate-900 mb-1">
                        {task.title}
                      </div>
                      {task.phase_label && (
                        <div className="mb-1">
                          <Badge variant="outline" className={`text-xs ${getPhaseColor(task.phase_label)}`}>
                            {task.phase_label}
                          </Badge>
                        </div>
                      )}
                      <div className="text-xs text-slate-600 mb-1">
                        {getAssigneeName(task.assigned_to)}
                      </div>
                      <div className="text-xs text-slate-500">
                        {task.estimated_hours}h
                      </div>
                    </div>
                  ))}
                  
                  {/* Add Task Button */}
                  <button
                    onClick={() => handleAddToSchedule(date)}
                    className="w-full p-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-[#247C75] hover:bg-slate-100 transition-colors text-sm text-slate-500 hover:text-[#247C75]"
                  >
                    + Add Task
                  </button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Team Availability and Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Team Availability */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Team Availability</CardTitle>
          </CardHeader>
          <CardContent>
            {teamAvailability.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No team members found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {teamAvailability.map(member => (
                  <div key={member.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <span className="text-sm font-medium text-slate-900">{member.name}</span>
                    <Badge 
                      variant={member.isAvailable ? 'default' : 'secondary'}
                      className={member.isAvailable 
                        ? 'bg-green-100 text-green-700 border-green-300' 
                        : 'bg-amber-100 text-amber-700 border-amber-300'
                      }
                    >
                      {member.isAvailable ? 'Available' : 'Busy'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Quick Stats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {/* Scheduled Hours */}
              <div className="text-center p-6 bg-slate-50 rounded-lg">
                <Clock className="h-8 w-8 mx-auto mb-2 text-slate-600" />
                <p className="text-3xl font-bold text-slate-900">{scheduledHours}</p>
                <p className="text-sm text-slate-600 mt-1">Scheduled Hours</p>
              </div>

              {/* Team Members */}
              <div className="text-center p-6 bg-slate-50 rounded-lg">
                <Users className="h-8 w-8 mx-auto mb-2 text-slate-600" />
                <p className="text-3xl font-bold text-slate-900">{teamMembers.length}</p>
                <p className="text-sm text-slate-600 mt-1">Team Members</p>
              </div>

              {/* Utilization */}
              <div className="text-center p-6 bg-slate-50 rounded-lg">
                <TrendingUp className="h-8 w-8 mx-auto mb-2 text-slate-600" />
                <p className="text-3xl font-bold text-slate-900">{utilization}%</p>
                <p className="text-sm text-slate-600 mt-1">Utilization</p>
              </div>

              {/* Conflicts */}
              <div className="text-center p-6 bg-slate-50 rounded-lg">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 text-slate-600" />
                <p className="text-3xl font-bold text-slate-900">{conflicts.length}</p>
                <p className="text-sm text-slate-600 mt-1">Conflicts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>

      {/* Unscheduled Work Sidebar */}
      <div className="w-80 flex-shrink-0">
        <Card className="border-0 shadow-sm sticky top-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Unscheduled Work</CardTitle>
            <p className="text-sm text-slate-600 mt-1">Projects ready for planning</p>
          </CardHeader>
          <CardContent>
            {unscheduledProjects.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Clock className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">No unscheduled projects</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {unscheduledProjects.map(project => (
                  <Card 
                    key={project.id} 
                    className="p-4 border border-slate-200 hover:border-[#247C75] hover:shadow-md transition-all cursor-pointer"
                    onClick={() => setSelectedProject(project)}
                  >
                    <h4 className="font-semibold text-sm text-slate-900 mb-2">{project.project_name}</h4>
                    <div className="space-y-1 text-xs text-slate-600">
                      <div className="flex items-center justify-between">
                        <span>Labour Hours:</span>
                        <span className="font-semibold">{project.estimated_labour_hours}h</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Team Size:</span>
                        <span className="font-semibold">{project.estimated_team_size} people</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Onsite Hours:</span>
                        <span className="font-semibold">{project.estimated_onsite_hours}h</span>
                      </div>
                      {project.estimated_labour_cost && (
                        <div className="flex items-center justify-between pt-2 border-t mt-2">
                          <span>Est. Cost:</span>
                          <span className="font-semibold text-[#247C75]">${project.estimated_labour_cost.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      className="w-full mt-3 bg-[#247C75] hover:bg-[#1d6259]"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Navigate to project detail
                        window.location.href = `/projects/${project.id}`;
                      }}
                    >
                      View Project
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </div>

      {/* Add Task Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add Task to Schedule</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Project Selection */}
              <div className="space-y-2">
                <Label>Project (Optional)</Label>
                <Select 
                  value={formData.project_id || 'none'} 
                  onValueChange={(v) => {
                    const projectId = v === 'none' ? '' : v;
                    setFormData({ ...formData, project_id: projectId });
                    handleProjectChange(projectId);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a project..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (General Task)</SelectItem>
                    {unscheduledProjects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.project_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Project Service / Phase Selection */}
              {formData.project_id && projectServices.length > 0 && (
                <>
                  <div className="space-y-2">
                    <Label>Project Service / Phase</Label>
                    <Select 
                      value={formData.project_service_id || 'all_services'} 
                      onValueChange={(v) => {
                        const serviceId = v === 'all_services' ? '' : v;
                        handleServiceChange(serviceId);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select service..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_services">All Services</SelectItem>
                        {projectServices.map(service => (
                          <SelectItem key={service.id} value={service.id}>
                            {service.name} ({service.remaining_units} of {service.total_units} {service.unit_label}s remaining)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* AI Phase Suggestions */}
                  {aiPhaseSuggestions.length > 0 && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="text-purple-600">✨</span>
                        AI Suggested Phase Labels
                      </Label>
                      <div className="grid grid-cols-1 gap-2">
                        {aiPhaseSuggestions.map((suggestion, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setFormData({ ...formData, phase_label: suggestion.label })}
                            className={`text-left p-3 rounded-lg border-2 transition-all ${
                              formData.phase_label === suggestion.label
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-slate-200 hover:border-purple-300 bg-white'
                            }`}
                          >
                            <div className="text-sm font-medium text-slate-900">{suggestion.label}</div>
                            <div className="text-xs text-slate-500 mt-1">Covers {suggestion.units} units</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Custom Phase Label (Optional)</Label>
                    <Input
                      value={formData.phase_label || ''}
                      onChange={(e) => setFormData({ ...formData, phase_label: e.target.value })}
                      placeholder="e.g., Phase 1 - Units 1-5, Block A, etc."
                    />
                    <p className="text-xs text-slate-500">
                      Or enter your own custom phase label
                    </p>
                  </div>

                  {formData.project_service_id && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-purple-900">
                        <CalendarIcon className="h-4 w-4" />
                        <span className="font-medium">
                          This task will be linked to the selected service for tracking scheduled work vs. billed units
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="space-y-2">
                <Label>Task Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter task title..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={formData.scheduled_start_time}
                    onChange={(e) => setFormData({ ...formData, scheduled_start_time: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Duration (hours)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={formData.estimated_hours}
                    onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateScheduledTask} className="bg-[#247C75] hover:bg-[#1d6259]">
                  Add Task
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
    </>
  );
};

export default OpsSchedule;